import type { CameraRestartInfo, CameraRestartReason } from '@/Pages/Selfie/helpers/cameraAccess'
import type { LivenessErrorType } from '@/types/liveness'

/**
 * Texto do erro para QUEM ATENDE o convidado, não para o convidado.
 *
 * Diferente do `copy.ts` da página: aquele é a mensagem gentil na tela ("vamos
 * tentar de novo"); esta é a linha que o organizador lê na modal de logs do
 * painel e precisa ser diagnóstica — dizer o que fazer diferente na próxima
 * tentativa.
 */
const OPERATOR_MESSAGES: Record<LivenessErrorType, string> = {
  poor_lighting: 'Iluminação insuficiente para a prova de vida.',
  face_misaligned:
    'Rosto fora do enquadramento (longe, cortado ou mais de uma pessoa na câmera).',
  timeout_abandon: 'O desafio de prova de vida expirou ou foi abandonado.',
  device_error: 'Problema de câmera no aparelho (permissão negada, sem câmera ou taxa de quadros).',
  liveness_access_denied:
    'Credencial da prova de vida recusada — configuração da AWS, não o convidado.',
  generic_liveness: 'Falha não identificada na prova de vida.',
}

export function describeLivenessError(errorType: LivenessErrorType): string {
  return OPERATOR_MESSAGES[errorType]
}

const CAMERA_RESTART_CAUSES: Record<CameraRestartReason, string> = {
  stalled: 'A câmera abriu mas não mostrou imagem',
  camera_error: 'O aparelho recusou abrir a câmera (ocupada ou lenta)',
  track_ended: 'A câmera foi interrompida antes de mostrar imagem',
  warmup_failed: 'O aparelho não liberou a câmera',
}

/**
 * A câmera não abriu de primeira e o app se recuperou sozinho. Não é falha
 * que o convidado viu — é o rastro para quem atende saber que aquele aparelho
 * tropeça na câmera (o user-agent vai junto no log).
 */
export function describeCameraRestart(info: CameraRestartInfo): string {
  const cause = CAMERA_RESTART_CAUSES[info.reason]
  return info.reloading
    ? `${cause}; o app recarregou a página para liberar a câmera.`
    : `${cause}; o app reabriu a câmera sozinho (tentativa ${info.attempt}).`
}

/**
 * Contexto técnico do erro, para o caso em que a mensagem não basta.
 *
 * ⚠️ Extrai APENAS campos conhecidos e escalares. O objeto de erro do Amplify
 * carrega o evento inteiro do detector; despejá-lo aqui arriscaria enfiar frame
 * ou dado do convidado num log que, por contrato, não pode ter biometria.
 */
export function livenessErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (error === null || typeof error !== 'object') return undefined

  const details: Record<string, unknown> = {}

  if ('state' in error && typeof error.state === 'string') {
    details.state = error.state
  }
  if ('name' in error && typeof error.name === 'string') {
    details.name = error.name
  }
  const nested = 'error' in error && error.error !== null && typeof error.error === 'object'
    ? error.error
    : null
  if (nested !== null && 'name' in nested && typeof nested.name === 'string') {
    details.awsError = nested.name
  }

  return Object.keys(details).length > 0 ? details : undefined
}
